import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Camera, User, Envelope, Phone, Lock, FloppyDisk, CircleNotch, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAccountPage = () => {
  const { user, checkAuth } = useAuth();
  const fileRef = useRef(null);
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', avatar_url: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [changingPwd, setChangingPwd] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        avatar_url: user.avatar_url || '',
      });
    }
  }, [user]);

  const handleAvatar = async (file) => {
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile(p => ({ ...p, avatar_url: data.url }));
      // Persist immediately so the sidebar reflects the change
      await axios.put(`${API_URL}/api/account/profile`, { avatar_url: data.url }, { withCredentials: true });
      await checkAuth();
      toast.success('Photo mise à jour');
    } catch {
      toast.error("Impossible d'uploader la photo");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      await axios.put(`${API_URL}/api/account/profile`, { avatar_url: '' }, { withCredentials: true });
      setProfile(p => ({ ...p, avatar_url: '' }));
      await checkAuth();
      toast.success('Photo supprimée');
    } catch {
      toast.error('Erreur');
    }
  };

  const saveProfile = async () => {
    if (!profile.name?.trim()) { toast.error('Le nom est obligatoire'); return; }
    if (!profile.email?.trim()) { toast.error("L'email est obligatoire"); return; }
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/api/account/profile`, {
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
      }, { withCredentials: true });
      await checkAuth();
      toast.success('Informations mises à jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (!pwd.current || !pwd.next) { toast.error('Tous les champs sont obligatoires'); return; }
    if (pwd.next.length < 8) { toast.error('Le nouveau mot de passe doit faire au moins 8 caractères'); return; }
    if (pwd.next !== pwd.confirm) { toast.error('La confirmation ne correspond pas'); return; }
    setChangingPwd(true);
    try {
      await axios.put(`${API_URL}/api/account/password`, {
        current_password: pwd.current,
        new_password: pwd.next,
      }, { withCredentials: true });
      setPwd({ current: '', next: '', confirm: '' });
      toast.success('Mot de passe modifié');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setChangingPwd(false);
    }
  };

  const avatarSrc = profile.avatar_url
    ? (profile.avatar_url.startsWith('/') ? `${API_URL}${profile.avatar_url}` : profile.avatar_url)
    : null;

  const initials = (profile.name || profile.email || 'A').charAt(0).toUpperCase();

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-account-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Mon compte</h1>
        <p className="text-slate-500 mt-1">Gérez vos informations personnelles, votre photo et votre mot de passe.</p>
      </div>

      {/* ── Profile card ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><User size={18} /> Informations personnelles</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Avatar */}
          <div className="flex items-start gap-5">
            <div className="relative group">
              <div
                className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-md flex items-center justify-center text-white text-3xl font-bold"
                style={{ backgroundColor: '#c2185b' }}
                data-testid="admin-avatar-preview"
              >
                {avatarSrc ? (
                  <img src={avatarSrc} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span>{initials}</span>
                )}
              </div>
              {uploadingAvatar && (
                <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                  <CircleNotch size={28} className="text-white animate-spin" />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => handleAvatar(e.target.files?.[0])}
                data-testid="avatar-file-input"
              />
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploadingAvatar}
                data-testid="upload-avatar-btn"
                className="gap-2"
              >
                <Camera size={16} /> Changer la photo
              </Button>
              {profile.avatar_url && (
                <Button
                  variant="ghost"
                  onClick={handleRemoveAvatar}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-2 justify-start"
                  data-testid="remove-avatar-btn"
                >
                  <Trash size={16} /> Supprimer
                </Button>
              )}
              <p className="text-xs text-slate-400 mt-1">JPG, PNG ou WebP. Max 5 Mo.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
            <div>
              <Label className="text-sm">Nom complet *</Label>
              <Input
                value={profile.name}
                onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                placeholder="Votre nom"
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <Label className="text-sm flex items-center gap-1.5"><Envelope size={14} /> Email *</Label>
              <Input
                type="email"
                value={profile.email}
                onChange={e => setProfile(p => ({ ...p, email: e.target.value }))}
                placeholder="vous@exemple.fr"
                data-testid="profile-email-input"
              />
            </div>
            <div>
              <Label className="text-sm flex items-center gap-1.5"><Phone size={14} /> Téléphone</Label>
              <Input
                type="tel"
                value={profile.phone}
                onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                placeholder="06 12 34 56 78"
                data-testid="profile-phone-input"
              />
            </div>
            <div>
              <Label className="text-sm">Rôle</Label>
              <Input value={user?.role === 'merchant' ? 'Marchand (admin boutique)' : user?.role || ''} disabled className="bg-slate-50" />
            </div>
          </div>

          <div className="pt-4 flex justify-end">
            <Button
              onClick={saveProfile}
              disabled={savingProfile}
              className="bg-[#0052FF] hover:bg-[#0040CC] gap-2"
              data-testid="save-profile-btn"
            >
              {savingProfile ? <CircleNotch className="animate-spin" size={16} /> : <FloppyDisk size={16} />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Password card ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Lock size={18} /> Sécurité</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm">Mot de passe actuel</Label>
              <Input
                type="password"
                value={pwd.current}
                onChange={e => setPwd(p => ({ ...p, current: e.target.value }))}
                placeholder="••••••••"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label className="text-sm">Nouveau mot de passe</Label>
              <Input
                type="password"
                value={pwd.next}
                onChange={e => setPwd(p => ({ ...p, next: e.target.value }))}
                placeholder="8 caractères minimum"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label className="text-sm">Confirmer</Label>
              <Input
                type="password"
                value={pwd.confirm}
                onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))}
                placeholder="Répétez"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={changePassword}
              disabled={changingPwd}
              variant="outline"
              className="gap-2"
              data-testid="change-password-btn"
            >
              {changingPwd ? <CircleNotch className="animate-spin" size={16} /> : <Lock size={16} />}
              Modifier le mot de passe
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAccountPage;
